import React, { useEffect, useMemo, useRef, useState } from "react";
import { Alert, Button, CircularProgress, MenuItem } from "@mui/material";
import { AnchoredMenu } from "../../common/dashboard/AnchoredMenu";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import {
  ExportCmpReportCsvInput,
  CmpInvoiceKindEnum,
  CmpReportTypeEnum,
  ChamberMemberCategory,
  CmpInvoiceAccountingExportProfileEnum,
  useExportCmpInvoicesAccountingCsvMutation,
  useExportCmpReportCsvLazyQuery,
  useGetCmpReportLookupsQuery,
} from "../../../graphql/types";
import useGetUserFromUserIdInAuth from "../../../hooks/useGetUserFromUserIdInAuth";
import { normalizeCmpTimeZone } from "../../../utils/cmpTimeZones";
import { accountingExportApolloContext, buildAccountingSoftwareExportDateRangeInput, downloadCompletedInvoiceExport, getInvoiceExportReviewGuidance, shouldShowAccountingSoftwareExport } from "../Invoicing/ManageInvoices/accountingExport";
import { getPreserveExpectedErrorsForCallerContext } from "../../../utils/apolloErrorPolicy";
import { buildStaffErrorFallback, getUserFacingErrorText } from "../../../utils/userFacingError";
import { ManagePageShell } from "../../common/dashboard/ManagePageShell";
import { ActionSuccessDialog } from "../../common/dashboard/ActionSuccessDialog";
import DashboardTabs from "../../common/dashboard/DashboardTabs";
import {
  ClearFiltersButton,
  clearFiltersButtonCompactSx,
} from "../../common/dashboard/FilterBar";
import {
  InvoiceDesignScope,
  invoiceDesignFontFamily,
  invoiceDesignTokens,
  invoiceModalActionSx,
  invoiceOutlinedActionSx,
} from "../Invoicing/InvoiceDesignSystem";
import { invoicePageLayoutPresentation } from "../Invoicing/invoicePageHeaderPresentation";
import { printInvoicePreviewDocument } from "../Invoicing/ManageInvoices/ManageInvoiceActionModals";
import { downloadTextFile } from "../Invoicing/invoiceFileDownloads";
import ReportFilters, {
  ReportFilterValues,
  ReportPeriodFields,
  getReportFilterSummaryLabel,
  getReportFilterSummaryParts,
} from "./ReportFilters";
import ReportSelector from "./ReportSelector";
import ReportSectionCard from "./ReportSectionCard";
import ReportPreviewModal, { ReportPreviewState } from "./ReportPreviewModal";
import { parseReportCsv } from "./parseReportCsv";
import {
  getReportDefinitions,
  getReportPreviewFilterSummary,
  ReportDefinition,
} from "./reportDefinitions";
import {
  buildReportDateRangeInput,
  createDefaultReportDateRangeValues,
  formatReportPeriod,
  getReportDateRangeValidationError,
} from "./reportDateRanges";
import { printEventAttendeePreview } from "./eventAttendeePrint";
import {
  ACCOUNTING_EXPORT_AVAILABLE_HOVER_TEXT,
  getAccountingExportOffHoverText,
} from "./reportAccountingExportText";
import { useCmpOrganizationNoun } from "../../../contexts/CmpOrganizationNounContext";

type StatusMessage = {
  severity: "error" | "info";
  message: string;
};

type DownloadSuccess = { filename: string; note?: string };

const REPORT_DOWNLOAD_FALLBACK = buildStaffErrorFallback("download the report");
const REPORT_PREVIEW_FALLBACK = buildStaffErrorFallback("load the report preview");
const ACCOUNTING_EXPORT_FALLBACK = buildStaffErrorFallback("download the accounting file", "No file was downloaded.");

const reportQueryErrorContext = getPreserveExpectedErrorsForCallerContext({
  expectedGraphQLErrorCodes: [],
});

export const createDefaultFilterValues = (): ReportFilterValues => ({ ...createDefaultReportDateRangeValues(), selections: {} });
export const buildExportInput = ({ chamberId, report, values, timeZone }: { chamberId: string; report: ReportDefinition; values: ReportFilterValues; timeZone?: string | null }): ExportCmpReportCsvInput => {
  const pick = (key: typeof report.filters[number]) => report.filters.includes(key) ? values.selections[key] : undefined;
  const kinds = pick("invoiceType")?.flatMap(value => value === "MEMBERSHIP" ? [CmpInvoiceKindEnum.Application, CmpInvoiceKindEnum.AddOn, CmpInvoiceKindEnum.Renewal] : Object.values(CmpInvoiceKindEnum).filter(kind => kind === value));
  return { chamberId, reportType: report.reportType, ...buildReportDateRangeInput(values, normalizeCmpTimeZone(timeZone).timeZone),
    membershipTypeIds: pick("membershipType"), memberSizeRanges: pick("memberSize"),
    membershipCategories: pick("membershipCategory")?.filter((value): value is ChamberMemberCategory => Object.values(ChamberMemberCategory).includes(value as ChamberMemberCategory)),
    memberSinceRanges: pick("memberSince"), paymentMethods: pick("paymentMethod"), paymentTerms: pick("paymentTerm"),
    salesAccountNumbers: pick("salesAccount"), eventIds: pick("eventName"), ticketTypeIds: pick("ticketType"),
    sponsorshipPackageIds: pick("sponsorshipLevel"), advertisingOptionIds: pick("advertisingOptions"), memberStatuses: pick("memberStatus"),
    invoiceKinds: kinds, agingPeriods: pick("agingPeriod"), submittedQuestionsOnly: pick("submittedQuestions")?.includes("true"),
  };
};

type ReportExportAction = "download" | "preview";

const Reports = () => {
  const noun = useCmpOrganizationNoun();
  const user = useGetUserFromUserIdInAuth();
  const chamber = user?.chamberInfo;
  const activeChamberRef = useRef(chamber?.id);
  activeChamberRef.current = chamber?.id;
  const [downloadAnchor, setDownloadAnchor] = useState<HTMLElement | null>(null);
  const [exportAccounting] = useExportCmpInvoicesAccountingCsvMutation({ context: accountingExportApolloContext });
  const chamberId = chamber?.id || "";
  // Design (21) CLAUDE.md "Reports page opens on Report Period": no report
  // type is preselected (Monday 13321955786).
  const [pickedReport, setSelectedReport] = useState<ReportDefinition | null>(null);
  // Design (21) "Report Period required" (Monday 13332444159): View Report and
  // Download first need both dates and a report type.
  const [needPeriod, setNeedPeriod] = useState(false);
  const [needReport, setNeedReport] = useState(false);
  const periodCardRef = useRef<HTMLElement>(null);
  const [filterValues, setFilterValues] = useState<ReportFilterValues>(createDefaultFilterValues);
  const [statusMessage, setStatusMessage] = useState<StatusMessage | null>(null);
  const [downloadSuccess, setDownloadSuccess] = useState<DownloadSuccess | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [previewState, setPreviewState] = useState<ReportPreviewState | null>(null);
  // A download or accounting error from inside the open preview shows in the
  // preview, not in the page Alert behind it.
  const [previewActionError, setPreviewActionError] = useState<string | null>(null);
  const previewOpenRef = useRef(false);
  previewOpenRef.current = previewState !== null;
  const showActionError = (message: string) => {
    if (previewOpenRef.current) setPreviewActionError(message);
    else setStatusMessage({ severity: "error", message });
  };
  const [exportAction, setExportAction] = useState<ReportExportAction | null>(null);
  const exportInFlightRef = useRef(false);
  useEffect(() => { setPreviewState(null); setPreviewActionError(null); setStatusMessage(null); setDownloadSuccess(null); }, [chamberId]);
  const headerOverride = useMemo(
    () => ({
      ...invoicePageLayoutPresentation,
      description: "Select a report period, choose a report type, and apply filters. View the report or download it.",
      titleStyle: {
        ...invoicePageLayoutPresentation.titleStyle,
        color: invoiceDesignTokens.ink,
      },
      descriptionStyle: {
        ...invoicePageLayoutPresentation.descriptionStyle,
        color: invoiceDesignTokens.ink,
        lineHeight: "19px",
      },
      withHeaderDivider: true,
      // Inset the existing header border without changing other route gutters.
      headerDividerClassName: "border-[#ECECEC] mx-4 !px-0 md:mx-10",
    }),
    [],
  );
  const {
    data: lookupsData,
    loading: lookupsLoading,
    error: lookupsError,
    refetch: refetchLookups,
  } = useGetCmpReportLookupsQuery({
    variables: { input: { chamberId } },
    skip: !chamberId,
    fetchPolicy: "cache-and-network",
    context: reportQueryErrorContext,
  });
  // IF11 RP2: Deleted, Past and the Member Status filter follow the member status model.
  const memberStatusModel = !!lookupsData?.getCmpReportLookups.memberStatusModel;
  const reportDefinitions = useMemo(() => getReportDefinitions({ memberStatusModel }), [memberStatusModel]);
  const selectedReport = reportDefinitions.find(item => item.id === pickedReport?.id) ?? null;
  const [exportReport, { loading: exportLoading }] =
    useExportCmpReportCsvLazyQuery({
      fetchPolicy: "no-cache",
      context: reportQueryErrorContext,
    });

  const clearFiltersKeepingDates = (values: ReportFilterValues): ReportFilterValues => ({
    ...createDefaultFilterValues(),
    startDate: values.startDate,
    endDate: values.endDate,
    compareStartDate: values.compareStartDate,
    compareEndDate: values.compareEndDate,
  });

  const periodReady = !!filterValues.startDate && !!filterValues.endDate;
  // Returns false and shows the design messages when the report cannot run.
  const ensureReportReady = (): ReportDefinition | null => {
    if (!periodReady) {
      setNeedPeriod(true);
      setNeedReport(!selectedReport);
      periodCardRef.current?.scrollIntoView?.({ behavior: "smooth", block: "start" });
      return null;
    }
    if (!selectedReport) {
      setNeedReport(true);
      return null;
    }
    return selectedReport;
  };

  const handleReportChange = (report: ReportDefinition) => {
    if (report.id === selectedReport?.id) return;
    setSelectedReport(report);
    setNeedReport(false);
    setFilterValues(clearFiltersKeepingDates);
    setStatusMessage(null);
    setValidationError(null);
  };

  const handleClearFilters = () => {
    setFilterValues(clearFiltersKeepingDates);
    setStatusMessage(null);
    setValidationError(null);
  };

  const handleFilterChange = (values: ReportFilterValues) => {
    setFilterValues(values);
    setValidationError(null);
    if (validationError) setStatusMessage(null);
  };

  // Both actions use the same validated input and synchronous guard. A second
  // click cannot send another query before Apollo updates its loading state.
  const handleExport = async ({ action }: { action: ReportExportAction }) => {
    if (exportInFlightRef.current) return;
    const report = ensureReportReady();
    if (!report) return;
    if (!chamberId) {
      setStatusMessage({ severity: "error", message: `Your ${noun.singular} details are still loading. Wait a moment, then try again.` });
      return;
    }
    const error = getReportDateRangeValidationError(filterValues);
    if (error) {
      setValidationError(error);
      setStatusMessage({ severity: "error", message: error });
      return;
    }

    exportInFlightRef.current = true;
    setExportAction(action);
    setValidationError(null);
    setStatusMessage(null);
    setPreviewActionError(null);
    if (action === "preview") setPreviewState({ status: "loading" });

    try {
      const result = await exportReport({
        variables: { input: buildExportInput({ chamberId, report, values: filterValues, timeZone: user?.chamberMemberProTimeZone }) },
      });
      if (activeChamberRef.current !== chamberId) return;
      if (result.error) throw result.error;
      const exportResult = result.data?.exportCmpReportCsv;
      if (!exportResult) throw new Error("The report export did not return a CSV file.");

      if (action === "preview") {
        const parsed = parseReportCsv(exportResult.csvContent);
        // Closing a loading preview must not reopen it when its query finishes.
        setPreviewState((current) => current ? {
          status: "ready",
          ...parsed,
          rowCount: exportResult.rowCount,
          generatedAtUtcMilli: exportResult.generatedAtUtcMilli,
          filename: exportResult.filename,
          document: exportResult.document,
          csvContent: exportResult.csvContent,
        } : null);
      } else {
        downloadTextFile({ content: exportResult.csvContent, contentType: exportResult.contentType, filename: exportResult.filename });
        // A zero-row file is still a completed download; the note keeps the
        // measured empty result distinct from missing data.
        setDownloadSuccess({
          filename: exportResult.filename,
          note: exportResult.rowCount > 0 ? undefined : "No rows match the report period and filters you chose. The file has column headings only.",
        });
      }
    } catch (error) {
      if (action === "preview") {
        const message = getUserFacingErrorText(error, REPORT_PREVIEW_FALLBACK);
        setPreviewState((current) => current ? { status: "error", message } : null);
      } else {
        showActionError(getUserFacingErrorText(error, REPORT_DOWNLOAD_FALLBACK));
      }
    } finally {
      exportInFlightRef.current = false;
      setExportAction(null);
    }
  };

  // Monday 13400145507, Report Preview Sheets (24) :2635: the Event Attendee
  // List prints the design attendee print; every other report prints the
  // preview sheet.
  const printPreview = () => {
    if (selectedReport?.reportType !== CmpReportTypeEnum.EventAttendeeList || previewState?.status !== "ready") {
      printInvoicePreviewDocument();
      return;
    }
    const opened = printEventAttendeePreview({
      eventLabel: previewState.document?.metadata?.find(item => item.label === "Events")?.value || "All events in period",
      filters: selectedReport.filters.filter(key => filterValues.selections[key]?.length).map(key => getReportFilterSummaryParts({ definition: selectedReport, key, lookups: lookupsData?.getCmpReportLookups, selected: filterValues.selections[key]! })),
      periodLabel: formatReportPeriod({ startDate: filterValues.startDate, endDate: filterValues.endDate }),
      preview: previewState,
      timeZone: user?.chamberMemberProTimeZone,
    });
    if (!opened) setPreviewActionError("The print window was blocked. Allow pop-ups for this site, then click Print again.");
  };

  const handleAccountingExport = async (profile: CmpInvoiceAccountingExportProfileEnum) => {
    setDownloadAnchor(null);
    if (exportInFlightRef.current || !shouldShowAccountingSoftwareExport(chamberId)) return;
    const report = ensureReportReady();
    if (!report) return;
    setStatusMessage(null);
    setPreviewActionError(null);
    setValidationError(null);
    try {
      const range = buildAccountingSoftwareExportDateRangeInput({
        dateLabels: { end: "Report End Date", start: "Report Start Date" },
        startDate: filterValues.startDate,
        endDate: filterValues.endDate,
        timeZone: normalizeCmpTimeZone(user?.chamberMemberProTimeZone).timeZone,
      });
      exportInFlightRef.current = true;
      setExportAction("download");
      const result = await exportAccounting({ variables: { input: {
        chamberId, profile, ...range,
        report: buildExportInput({ chamberId, report, values: filterValues, timeZone: user?.chamberMemberProTimeZone }),
        isCmpInvoiceModuleTestData: undefined,
      } } });
      if (activeChamberRef.current !== chamberId) return;
      const exported = downloadCompletedInvoiceExport({ download: downloadTextFile, payload: result.data?.exportCmpInvoicesAccountingCsv });
      setDownloadSuccess({ filename: exported.filename, note: `${exported.rowCount} ${exported.rowCount === 1 ? "invoice" : "invoices"} in the file. ${getInvoiceExportReviewGuidance(profile)}${exported.payerDataWarning ? ` ${exported.payerDataWarning}` : ""}` });
    } catch (error) {
      showActionError(getUserFacingErrorText(error, ACCOUNTING_EXPORT_FALLBACK));
    } finally {
      exportInFlightRef.current = false;
      setExportAction(null);
    }
  };

  const actionsDisabled = !chamberId || lookupsLoading || exportLoading || exportAction !== null;

  return (
    <InvoiceDesignScope>
      <ManagePageShell
        headerOverride={headerOverride}
        statusMessage={statusMessage}
        onStatusMessageClose={() => setStatusMessage(null)}
        statusMessageClassName="mx-4 mb-5 md:mx-0"
        className="w-full pb-10"
      >
        {/* Offset PageContainer's 8 px body padding so the tab gap is 24 px. */}
        <div
          className="-mt-2 flow-root"
          style={{
            fontFamily: invoiceDesignFontFamily,
            color: invoiceDesignTokens.ink,
          }}
        >
          <DashboardTabs
            ariaLabel="Report sections"
            activeTab="general"
            items={[
              { id: "general", label: "General Reports" },
              {
                id: "special",
                label: "Special Reports",
                disabled: true,
                badge: (
                  <>
                    {" "}
                    <span className="ml-1 inline-flex items-center whitespace-nowrap rounded-[10px] bg-[#E1A731] px-2 py-0.5 align-middle text-[11px] font-bold leading-[15px] text-white">
                      Coming Soon
                    </span>
                  </>
                ),
              },
            ]}
            onTabChange={() => undefined}
            barClassName="mt-6 border-b border-[#ECECEC]"
            navClassName="flex min-w-max items-end gap-6"
            getTabClassName={({ active }) =>
              `whitespace-nowrap text-[15px] leading-5 pb-3 focus:outline-none focus-visible:ring-2 focus-visible:ring-cpaGold ${
                active
                  ? "font-bold text-[#E1A731] border-b-[3px] border-[#E1A731]"
                  : "font-semibold text-[#0A0A0A] cursor-default border-b-[3px] border-transparent"
              }`
            }
          />
          <section
            aria-label="General report builder"
            className="mx-auto mt-[35px] flex w-full max-w-[1280px] flex-col gap-[26px] px-0 lg:px-3"
          >
            {!chamberId && (
              <Alert severity="info">Loading your reports...</Alert>
            )}
            {lookupsError && (
              <Alert
                severity="error"
                action={
                  <button
                    type="button"
                    className="font-bold underline"
                    onClick={() => {
                      // Apollo retains the query error and reports the failure.
                      void refetchLookups().catch(() => undefined);
                    }}
                  >
                    Retry
                  </button>
                }
              >
                Report filter options could not be loaded. Retry before
                downloading a filtered report.
              </Alert>
            )}
            <ReportSectionCard
              title="Report Period"
              sectionRef={periodCardRef}
              highlight={!periodReady}
              titleAdornment={periodReady ? null : (
                <span className="rounded-full bg-[#E1A731] px-[10px] py-[3px] text-[12px] font-bold text-white">Start here</span>
              )}
            >
              {periodReady ? null : (
                <p
                  className={`-mt-2 mb-0 text-[14px] ${needPeriod ? "font-semibold text-[#F0202E]" : "font-normal text-[#0A0A0A]"}`}
                >
                  Select a Report Start and End Date to begin.
                </p>
              )}
              <ReportPeriodFields
                values={filterValues}
                onChange={handleFilterChange}
                validationError={validationError}
                disabled={actionsDisabled}
              />
            </ReportSectionCard>
            <div className="grid min-w-0 grid-cols-1 items-stretch gap-[26px] lg:grid-cols-[minmax(0,1fr)_minmax(0,1.5fr)]">
              <ReportSectionCard title="General Reports" headingAction={<span className="text-[13px] text-[#727272]">{reportDefinitions.length} reports</span>}>
                <ReportSelector
                  definitions={reportDefinitions}
                  selectedReport={selectedReport}
                  onChange={handleReportChange}
                  disabled={actionsDisabled}
                />
              </ReportSectionCard>
              <ReportSectionCard
                title="Report Filters"
                description="Use filters below to apply to any Report Type"
                headingAction={
                  <ClearFiltersButton
                    label="Clear All Filter"
                    onClick={handleClearFilters}
                    sx={{
                      ...clearFiltersButtonCompactSx,
                      width: "auto",
                      flexShrink: 0,
                    }}
                  />
                }
              >
                <ReportFilters
                  definition={selectedReport}
                  values={filterValues}
                  onChange={handleFilterChange}
                  lookups={lookupsData?.getCmpReportLookups}
                  lookupsLoading={lookupsLoading}
                  validationError={validationError}
                  disabled={actionsDisabled}
                />
              </ReportSectionCard>
            </div>
            <div>
              {needPeriod && !periodReady && (
                <p className="mb-3 text-right text-[13px] font-semibold text-[#F0202E]" role="alert">
                  Select a Report Start and End Date in Report Period to view or download a report.
                </p>
              )}
              {needReport && !selectedReport && (
                <p className="mb-3 text-right text-[13px] font-semibold text-[#F0202E]" role="alert">
                  Select a Report Type to view or download a report.
                </p>
              )}
              {validationError && (
                <p
                  className="mb-3 text-right text-[14px] text-[#E24C4C]"
                  role="alert"
                >
                  {validationError}
                </p>
              )}
              <div className="flex flex-wrap justify-end gap-3">
                <Button
                  variant="outlined"
                  disabled={actionsDisabled}
                  onClick={() => void handleExport({ action: "preview" })}
                  sx={{
                    ...invoiceOutlinedActionSx,
                    gap: "8px",
                    height: 45,
                    lineHeight: "19px",
                    letterSpacing: "normal",
                    padding: "12px 20px",
                    whiteSpace: "nowrap",
                  }}
                >
                  <VisibilityOutlinedIcon sx={{ fontSize: 16 }} />
                  View Report
                </Button>
                <Button
                  disabled={actionsDisabled}
                  aria-haspopup={selectedReport?.group === "Financial" ? "menu" : undefined}
                  aria-expanded={!!downloadAnchor}
                  onClick={(event) => selectedReport?.group === "Financial" && periodReady ? setDownloadAnchor(event.currentTarget) : void handleExport({ action: "download" })}
                  sx={[
                    invoiceModalActionSx({
                      backgroundColor: "#E1A731",
                      hoverBackgroundColor: "#C9931F",
                    }),
                    {
                      gap: "8px",
                      height: 45,
                      minHeight: 45,
                      lineHeight: "19px",
                      letterSpacing: "normal",
                      padding: "12px 20px",
                      whiteSpace: "nowrap",
                    },
                  ]}
                >
                  {exportAction === "download" ? (
                    <CircularProgress size={18} color="inherit" />
                  ) : (
                    <DownloadOutlinedIcon sx={{ fontSize: 16 }} />
                  )}
                  {exportAction === "download"
                    ? "Preparing download..."
                    : "Download"}
                  {selectedReport?.group === "Financial" && <KeyboardArrowDownIcon sx={{ fontSize: 14 }} />}
                </Button>
                <AnchoredMenu anchorEl={downloadAnchor} open={!!downloadAnchor} onClose={() => setDownloadAnchor(null)}
                  placement="bottom-end" offset={6}
                  PaperProps={{ sx: { minWidth: 200, border: "1px solid #ECECEC", borderRadius: "10px", p: "6px", "& .MuiMenuItem-root": { fontFamily: invoiceDesignFontFamily, fontSize: 14, fontWeight: 600, p: "10px 12px", borderRadius: "7px" } } }}>
                  <MenuItem onClick={() => { setDownloadAnchor(null); void handleExport({ action: "download" }); }}>CSV File</MenuItem>
                  {[
                    { label: "Sage Accounting", profile: CmpInvoiceAccountingExportProfileEnum.SageBusinessCloud },
                    { label: "QuickBooks", profile: CmpInvoiceAccountingExportProfileEnum.QuickbooksOnline },
                  ].map(({ label, profile }) => <MenuItem key={profile}
                    disabled={!shouldShowAccountingSoftwareExport(chamberId)}
                    title={shouldShowAccountingSoftwareExport(chamberId) ? ACCOUNTING_EXPORT_AVAILABLE_HOVER_TEXT : getAccountingExportOffHoverText(noun.singular)}
                    onClick={() => void handleAccountingExport(profile)}>{label}</MenuItem>)}
                </AnchoredMenu>
              </div>
            </div>
          </section>
        </div>
        {previewState && chamber && selectedReport && (
          <ReportPreviewModal
            open
            onClose={() => { setPreviewState(null); setPreviewActionError(null); }}
            state={previewState}
            actionError={previewActionError}
            onActionErrorClose={() => setPreviewActionError(null)}
            reportTitle={selectedReport.title}
            chamber={chamber}
            startDate={filterValues.startDate}
            endDate={filterValues.endDate}
            timeZone={user?.chamberMemberProTimeZone}
            onPrint={printPreview}
            onDownload={() => void handleExport({ action: "download" })}
            onAccountingExport={selectedReport.group === "Financial" ? handleAccountingExport : undefined}
            accountingEnabled={shouldShowAccountingSoftwareExport(chamberId)}
            filterSummary={getReportPreviewFilterSummary({ report: selectedReport, selectedFilterLabels: selectedReport.filters.filter(key => filterValues.selections[key]?.length).map(key => getReportFilterSummaryLabel({ definition: selectedReport, key, lookups: lookupsData?.getCmpReportLookups, selected: filterValues.selections[key]! })) })}
            chamberId={chamberId}
            reportInput={buildExportInput({ chamberId, report: selectedReport, values: filterValues, timeZone: user?.chamberMemberProTimeZone })}
          />
        )}
        <ActionSuccessDialog
          message={downloadSuccess ? `A .csv file (${downloadSuccess.filename}) has been downloaded!` : null}
          highlights={downloadSuccess ? [downloadSuccess.filename] : undefined}
          note={downloadSuccess?.note}
          onClose={() => setDownloadSuccess(null)}
        />
      </ManagePageShell>
    </InvoiceDesignScope>
  );
};

export default Reports;
